import apiClient from '../apiClient';
import type { BaseResponse, ListResponse, PaginationRequest } from '../../types/base.dto';

// 创建参数接口
export interface CreateParams<T = any> {
  [key: string]: any;
}

// 更新参数接口
export interface UpdateParams {
  [key: string]: any;
}

interface TreeItem {
  id: number;
  parent_id?: number | null;
  sort?: number;
  children?: any[];
  [key: string]: any;
}

/**
 * 通用构建树形结构函数
 * @param items 平铺的列表数据
 * @returns 树形结构列表
 */
export const buildTree = <T extends TreeItem>(items: T[]): T[] => {
  const nodeMap = new Map<number, T>();
  // Clone items to avoid mutating original if needed, and init children
  items.forEach((item) => {
    nodeMap.set(item.id, { ...item, children: [] });
  });

  const roots: T[] = [];
  nodeMap.forEach((node) => {
    if (node.parent_id && nodeMap.has(node.parent_id)) {
      const parent = nodeMap.get(node.parent_id)!;
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: T[]) => {
    nodes.sort((a, b) => {
      const diff = (a.sort ?? 0) - (b.sort ?? 0);
      if (diff !== 0) return diff;
      return a.id - b.id;
    });
    nodes.forEach((n) => {
      if (n.children && n.children.length > 0) {
        sortNodes(n.children);
      }
    });
  };

  sortNodes(roots);
  return roots;
};

/**
 * BaseAPI 基础API类
 * 提供标准的CRUD操作，适配后端BaseHandler
 */
export class BaseAPI<T = any> {
  protected endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  /**
   * 获取列表
   * @param params 查询参数
   * @returns Promise<BaseResponse<ListResponse<T>>>
   */
  async list(params?: PaginationRequest): Promise<BaseResponse<ListResponse<T>>> {
    const requestParams: any = { ...params };
    const response = await apiClient.post(`${this.endpoint}/list`, requestParams);
    return response.data;
  }

  /**
   * 根据ID获取单个记录
   * @param id 记录ID
   * @returns Promise<BaseResponse<T>>
   */
  async getById(id: string): Promise<BaseResponse<T>> {
    const response = await apiClient.get(`${this.endpoint}/${id}`);
    return response.data;
  }

  /**
   * 创建记录
   * @param data 创建数据
   * @returns Promise<BaseResponse<any>>
   */
  async create(data: CreateParams<T>): Promise<BaseResponse<any>> {
    const response = await apiClient.post(this.endpoint, data);
    return response.data;
  }

  /**
   * 更新记录
   * @param id 记录ID
   * @param data 更新数据
   * @returns Promise<BaseResponse<any>>
   */
  async updateById(id: string, data: UpdateParams): Promise<BaseResponse<any>> {
    const response = await apiClient.put(`${this.endpoint}/${id}`, data);
    return response.data;
  }

  /**
   * 删除记录
   * @param id 记录ID
   * @returns Promise<BaseResponse<any>>
   */
  async deleteById(id: string): Promise<BaseResponse<any>> {
    const response = await apiClient.delete(`${this.endpoint}/${id}`);
    return response.data;
  }

  /**
   * 获取所有记录（不分页）
   * @param params 查询参数
   * @returns Promise<BaseResponse<ListResponse<T>>>
   */
  async getAll(params?: Omit<PaginationRequest, 'page' | 'pageSize'>): Promise<BaseResponse<ListResponse<T>>> {
    const allParams: any = {
      PageSize: 1000, // 设置大页面大小实现不分页
      ...params,
    };
    return this.list(allParams);
  }
}

export default BaseAPI;
